import { type ReactNode } from 'react';

/**
 * The footer's reveal-on-scroll overlay (2026-08-05 user spec: "when we scroll down the footer will
 * come up in an animation, which means we don't have to scroll until the bottom to see it. If no
 * scroll, we don't see the footer").
 *
 * So the footer is not in the document flow at all any more — it is pinned to the bottom of the
 * content column and slides up (300ms transform) while the page scrolls down, and back out on the first
 * scroll up (2026-09-30 user direction) or when the page returns to the top; AppShell decides which with
 * `useFooterReveal`. Two consequences that make this the third footer mechanism this week:
 *
 * - At the top of any page there is NO footer, which is the vertical space the user wanted back.
 * - It appears WITHOUT scrolling to the bottom — mid-scroll on a long dashboard the links are there.
 *
 * Transform (not `display`/`height`) so the slide animates on the compositor; `translate-y-full`
 * pushes it fully below the column, which is `overflow-hidden`, so hidden means invisible AND
 * unclickable (pointer-events kill clicks during the transition too). `aria-hidden` tracks
 * visibility so screen readers don't tab into an off-screen footer.
 */
export function ScrollRevealFooter({ visible, children }: { visible: boolean; children: ReactNode }) {
  return (
    <div
      aria-hidden={!visible}
      className={`absolute inset-x-0 bottom-0 z-30 transition-transform duration-300 ease-out motion-reduce:transition-none ${
        visible ? 'translate-y-0' : 'pointer-events-none translate-y-full'}`}
      style={{
        background: 'var(--canvas)',
        borderTop: '1px solid var(--canvas-divider-strong)',
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.06)',
      }}
    >
      {children}
    </div>
  );
}
